import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, Loader2, Warehouse, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ApiError, canManage } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Button, cx, Tip } from "./ui";

import { t, msg } from "@/lib/i18n";
const DISMISS_KEY = "lc.backendImport.dismissed";

/** Stand der Lager-Verbindung inkl. Artikelimport; fragt während eines Imports alle 2,5 s nach */
export function useBackendImport() {
  const { tenantId } = useTenant();
  const q = useQuery({
    queryKey: ["backend", tenantId],
    queryFn: () => api.backend.get(tenantId),
    refetchInterval: (query) => (query.state.data?.connection?.productImport?.status === "running" ? 2500 : false),
  });
  const conn = q.data?.connection;
  return { state: conn?.productImport ?? null, connected: conn?.status === "active" && conn.authorized !== false, warehouseName: conn?.warehouseName ?? null, isLoading: q.isLoading };
}

/** "Aus Lager laden": übernimmt alle Artikel aus dem FFN-Konto (Migration von FFN Connect) */
export function BackendImportButton({ open: openProp, onOpenChange, hideTrigger }: { open?: boolean; onOpenChange?: (o: boolean) => void; hideTrigger?: boolean } = {}) {
  const { tenantId, role } = useTenant();
  const qc = useQueryClient();
  const { state, connected, warehouseName } = useBackendImport();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const running = state?.status === "running";
  const start = useMutation({
    mutationFn: () => api.backend.importProducts(tenantId),
    onSuccess: () => {
      setOpen(false);
      try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ohne Storage egal */ }
      void qc.invalidateQueries({ queryKey: ["backend", tenantId] });
      toast.success("Import gestartet", { description: msg("Der Fortschritt erscheint oben auf der Seite.") });
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Import konnte nicht gestartet werden") : "Import konnte nicht gestartet werden"),
  });

  if (!canManage(role)) return null;
  const button = (
    <Button icon={running ? <Loader2 className="size-4 animate-spin" /> : <Warehouse className="size-4" />} disabled={!connected || running} onClick={() => setOpen(true)}>
      {running ? t("Import läuft …") : t("Aus Lager laden")}
    </Button>
  );

  return (
    <>
      {hideTrigger ? null : connected ? button : <Tip content={t("Zuerst unter Einstellungen → Lager das FFN-Konto verbinden")}>{<span>{button}</span>}</Tip>}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
          <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(500px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
            <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Warehouse className="size-4 text-fg-subtle" /> {t("Artikel aus dem Lager laden")}</Dialog.Title>
            <Dialog.Description className="mt-1 text-fg-muted text-[13px]">
              {t("Übernimmt alle Artikel aus dem JTL-FFN-Konto")}{warehouseName ? t(" (Lager {warehouseName})", { warehouseName }) : ""} {t("ins Portal – ideal für den Umstieg von FFN Connect.")}
            </Dialog.Description>
            <ul className="mt-4 space-y-2 text-[13px]">
              {[
                [msg("Neue Artikel"), msg("werden mit allen Stammdaten angelegt (Maße, Gewicht, EAN, Zolldaten, Charge/MHD).")],
                [msg("Vorhandene Artikel (gleiche SKU)"), msg("werden nur ergänzt – im Portal oder aus Shopify gepflegte Werte bleiben unverändert.")],
                [msg("Stücklisten"), msg("werden übernommen, sobald alle Komponenten importiert sind.")],
                [msg("Keine Doppelungen"), msg("– die Lager-Artikelnummer wird verknüpft, ein zweiter Lauf ist jederzeit gefahrlos.")],
              ].map(([b, t]) => (
                <li key={b} className="flex gap-2"><CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /> <span><b>{b}</b> {t}</span></li>
              ))}
            </ul>
            {state?.status === "done" && state.finishedAt && <p className="mt-4 text-[12.5px] text-fg-muted">{t("Zuletzt geladen")} {fmtRelative(state.finishedAt)}: {fmtNumber(state.created)} {t("neu,")} {fmtNumber(state.updated)} {t("ergänzt.")}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
              <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>{t("Import starten")}</Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

/** Fortschritt/Ergebnis des Imports; nach Abschluss werden Artikel-Listen neu geladen */
export function BackendImportBanner() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const { state } = useBackendImport();
  const [dismissed, setDismissed] = useState<string | null>(() => { try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; } });
  const lastStatus = useRef<string | null>(null);

  // Während des Imports regelmäßig, am Ende einmal die Artikelliste aktualisieren
  useEffect(() => {
    if (!state) return;
    if (state.status === "running" || lastStatus.current === "running") {
      void qc.invalidateQueries({ queryKey: ["products", tenantId] });
      void qc.invalidateQueries({ queryKey: ["products-summary", tenantId] });
    }
    lastStatus.current = state.status;
  }, [state, qc, tenantId]);

  if (!state || dismissed === state.runId) return null;
  // Abgeschlossene Läufe nur kurz zeigen
  const age = state.finishedAt ? Date.now() - new Date(state.finishedAt).getTime() : 0;
  if (state.status !== "running" && age > 60 * 60_000) return null;
  const dismiss = () => { setDismissed(state.runId); try { sessionStorage.setItem(DISMISS_KEY, state.runId); } catch { /* egal */ } };
  const processed = state.created + state.updated + state.unchanged;

  return (
    <div className={cx("rounded-xl border px-4 py-3 flex items-start gap-3 text-[13px]", state.status === "failed" ? "border-danger/25 bg-danger-soft" : state.status === "done" ? "border-success/25 bg-success-soft/60" : "border-border bg-surface")}>
      {state.status === "running" ? <Loader2 className="size-4 mt-0.5 animate-spin text-fg-muted" /> : state.status === "done" ? <CheckCircle2 className="size-4 mt-0.5 text-success" /> : <CircleAlert className="size-4 mt-0.5 text-danger" />}
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {state.status === "running" ? t("Artikel werden aus dem Lager übernommen …") : state.status === "done" ? t("Artikel aus dem Lager übernommen") : t("Import abgebrochen")}
        </p>
        <p className="text-fg-muted mt-0.5 tabular-nums">
          {fmtNumber(processed)} {t("verarbeitet ·")} <b className="text-fg">{fmtNumber(state.created)}</b> {t("neu ·")} {fmtNumber(state.updated)} {t("ergänzt ·")} {fmtNumber(state.unchanged)} {t("unverändert")}
          {state.skipped > 0 && <> · {fmtNumber(state.skipped)} {t("ohne SKU übersprungen")}</>}
          {state.bundlesLinked > 0 && <> · {fmtNumber(state.bundlesLinked)} {t("Stücklisten")}</>}
        </p>
        {state.pendingBundles > 0 && state.status === "done" && <p className="mt-1 text-warning text-[12.5px]">{state.pendingBundles} {t("Stücklisten konnten nicht zugeordnet werden – Komponenten fehlen im Lager.")}</p>}
        {state.status === "failed" && state.error && <p className="mt-1 text-danger text-[12.5px]">{state.error}</p>}
        {state.status === "done" && state.created > 0 && <p className="mt-1 text-[12.5px] text-fg-muted">{t("Tipp: Der Reiter „Blockiert“ zeigt, wo Stammdaten fürs Lager noch fehlen.")}</p>}
      </div>
      {state.status !== "running" && <button onClick={dismiss} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Ausblenden")}><X className="size-3.5" /></button>}
    </div>
  );
}
